let currentManifestBlobUrl = null;
let cachedBaseManifest = null;

const SHORT_NAME_MAX = 12;

const FALLBACK_BASE_MANIFEST = {
  short_name: "Expo365",
  name: "Expo365",
  start_url: "/",
  display: "standalone",
  theme_color: "#3067ae",
  background_color: "#ffffff",
  orientation: "any",
  description:
    "EXPO365 by ConnectsU is a mobile and video-first platform that helps organizations generate leads, attract talent, and bridge the digital engagement gap with a branded, white-label SaaS solution.",
  lang: "en-US",
  scope: "/",
  prefer_related_applications: false,
};

function truncateShortName(name) {
  const trimmed = String(name).trim();
  if (trimmed.length <= SHORT_NAME_MAX) return trimmed;
  return trimmed.slice(0, SHORT_NAME_MAX).trim();
}

function guessImageType(url) {
  const lower = String(url).toLowerCase();
  if (lower.includes(".svg")) return "image/svg+xml";
  if (lower.includes(".jpg") || lower.includes(".jpeg")) return "image/jpeg";
  if (lower.includes(".webp")) return "image/webp";
  if (lower.includes(".gif")) return "image/gif";
  return "image/png";
}

function isAbsoluteUrl(url) {
  return /^(https?:|data:|blob:)/i.test(String(url));
}

function absolutizeUrl(src) {
  if (!src || isAbsoluteUrl(src)) return src;
  const publicUrl = process.env.PUBLIC_URL || "";
  const path = String(src).replace(/^\/+/, "");
  const combined = `${window.location.origin}${publicUrl}/${path}`;
  return combined.replace(/([^:]\/)\/+/g, "$1");
}

function absolutizeSrcEntries(entries) {
  if (!Array.isArray(entries)) return entries;
  return entries.map((entry) =>
    entry && typeof entry.src === "string"
      ? { ...entry, src: absolutizeUrl(entry.src) }
      : entry
  );
}

function absolutizeManifestUrls(manifest) {
  return {
    ...manifest,
    icons: absolutizeSrcEntries(manifest.icons),
    screenshots: absolutizeSrcEntries(manifest.screenshots),
  };
}

async function loadBaseManifest() {
  if (cachedBaseManifest) return cachedBaseManifest;
  try {
    const base = process.env.PUBLIC_URL || "";
    const res = await fetch(`${base}/manifest.json`, { cache: "no-cache" });
    if (!res.ok) throw new Error("manifest fetch failed");
    cachedBaseManifest = await res.json();
  } catch (e) {
    cachedBaseManifest = { ...FALLBACK_BASE_MANIFEST };
  }
  return cachedBaseManifest;
}

function replaceManifestLink(href) {
  const existing = document.querySelector('link[rel="manifest"]');
  if (existing) {
    existing.remove();
  }
  const link = document.createElement("link");
  link.setAttribute("rel", "manifest");
  link.setAttribute("href", href);
  document.head.appendChild(link);
}

/**
 * Apply partner name + logo to the installable PWA manifest and icon links.
 * @param {{ name?: string, mediaLink?: string }} partner
 * @returns {Promise<boolean>} true if branding applied
 */
export async function applyPartnerPwaBranding(partner) {
  const name = partner?.name?.trim?.() ? partner.name.trim() : "";
  const mediaLink =
    typeof partner?.mediaLink === "string" ? partner.mediaLink.trim() : "";

  if (!name || !mediaLink) {
    return false;
  }

  const base = await loadBaseManifest();
  const iconType = guessImageType(mediaLink);
  const manifest = absolutizeManifestUrls({
    ...base,
    name,
    short_name: truncateShortName(name),
    icons: [
      { src: mediaLink, sizes: "192x192", type: iconType, purpose: "any" },
      { src: mediaLink, sizes: "512x512", type: iconType, purpose: "any" },
    ],
  });

  const blob = new Blob([JSON.stringify(manifest)], {
    type: "application/manifest+json",
  });
  const blobUrl = URL.createObjectURL(blob);

  if (currentManifestBlobUrl) {
    URL.revokeObjectURL(currentManifestBlobUrl);
  }
  currentManifestBlobUrl = blobUrl;

  replaceManifestLink(blobUrl);
  // Keep browser favicon / apple-touch-icon as Expo365 (public/index.html).
  // Partner logo is only used in the installable Web App Manifest icons.

  return true;
}

/** Test helper — clears blob URL + cached base manifest */
export function __resetPartnerPwaBrandingForTests() {
  if (currentManifestBlobUrl) {
    URL.revokeObjectURL(currentManifestBlobUrl);
    currentManifestBlobUrl = null;
  }
  cachedBaseManifest = null;
}
